import { formatPercentage } from '@helpers/utils/numbers/numbers';
import clsx from 'clsx';
import * as d3 from 'd3';
import { useId } from 'react';

import { getHumanFriendlyTicks } from '../helpers/chart-ticks/chart-ticks';
import {
  HighLowBar,
  HighLowMedianDataPoint,
} from './components/high-low-bar/HighLowBar';
import styles from './HighLowChart.module.scss';

export interface HighLowChartProps {
  /** Bars to render — each describes a high / low / median per period. */
  data: HighLowMedianDataPoint[];
  /** Chart width in pixels. */
  width: number;
  /** Chart height in pixels. */
  height: number;
  /** Inner horizontal padding (px) at the chart edges. Defaults to `{ left: 85, right: 150 }`. */
  padding?: {
    left: number;
    right: number;
  };
  /** Accessible `<title>` rendered inside the SVG. */
  alt?: string;
  /**
   * `primary` — median labels on bars, 2 decimal axis ticks.
   * `secondary` — connecting lines between bars, optional area fill, integer axis ticks.
   * `tertiary` — same as secondary for axis and bars, but no area fill and no high/low connector lines; dashed median connector between bars is shown.
   */
  variant?: 'primary' | 'secondary' | 'tertiary';
  /** Only when `variant` is `secondary`: fills between the connector lines and 0%. Ignored otherwise. Default true. */
  showAreaFill?: boolean;
}

const margin = {
  top: 20,
  right: 50,
  bottom: 30,
  left: 0,
};

const defaultPadding = {
  left: 85,
  right: 150,
};

/** Required separator between SVG path commands and coordinates in `d` strings */
const blankSpace = ' ';

interface LineSegmentsScaleFns {
  values: number[];
  xForIndex: (i: number) => number;
  yForValue: (v: number) => number;
  yZero: number;
}

/** Polylines connecting numeric samples across bar indices (splits at y=0 crossings). */
const createLineSegmentsBetweenPoints = ({
  values,
  xForIndex,
  yForValue,
  yZero,
}: LineSegmentsScaleFns): { path: string; isAboveZero: boolean }[] => {
  const segments: { path: string; isAboveZero: boolean }[] = [];

  for (let i = 0; i < values.length; i++) {
    const currentValue = values[i];
    const currentX = xForIndex(i);
    const currentY = yForValue(currentValue);
    const currentIsAboveZero = currentValue > 0;

    if (i === 0) {
      segments.push({
        path: `M${blankSpace}${currentX}${blankSpace}${currentY}`,
        isAboveZero: currentIsAboveZero,
      });
      continue;
    }

    const prevValue = values[i - 1];
    const prevX = xForIndex(i - 1);
    const prevIsAboveZero = prevValue > 0;

    if (prevIsAboveZero !== currentIsAboveZero) {
      const t = -prevValue / (currentValue - prevValue);
      const intersectionX = prevX + t * (currentX - prevX);

      const lastSegment = segments[segments.length - 1];
      lastSegment.path += `${blankSpace}L${blankSpace}${intersectionX}${blankSpace}${yZero}`;

      segments.push({
        path: `M${blankSpace}${intersectionX}${blankSpace}${yZero}${blankSpace}L${blankSpace}${currentX}${blankSpace}${currentY}`,
        isAboveZero: currentIsAboveZero,
      });
    } else {
      const lastSegment = segments[segments.length - 1];
      lastSegment.path += `${blankSpace}L${blankSpace}${currentX}${blankSpace}${currentY}`;
    }
  }

  return segments;
};

/**
 * High / low / median bar chart for distribution-style data
 * (e.g. expected return ranges across earnings windows).
 *
 * Each bar shows the period's high and low extremes with a median
 * marker, formatted as percentages. Three visual variants tune how
 * the data is presented:
 *
 * - `primary` — median labels on every bar, fine 2-dp axis.
 * - `secondary` — high / low / median connector lines between bars
 *   plus an optional gradient area fill.
 * - `tertiary` — bars only, with a dashed median connector between
 *   them (no high/low lines, no fill).
 */
export const HighLowChart = ({
  data,
  width,
  height,
  padding = defaultPadding,
  alt,
  variant = 'primary',
  showAreaFill = true,
}: HighLowChartProps) => {
  const usesSecondaryAxisStyle =
    variant === 'secondary' || variant === 'tertiary';
  const rendersConnectorLayer = variant === 'secondary';
  const rendersMedianConnectorOnly = variant === 'tertiary';
  const chartId = useId();
  const [xMin, xMax] = [0, data.length - 1];
  const yMax = d3.max(data, d => d.high) ?? 0;
  const yMin = d3.min(data, d => d.low) ?? 0;

  const { chartTicks, maxRounded, minRounded } = getHumanFriendlyTicks({
    min: yMin,
    max: yMax,
  });

  const xScale = d3
    .scaleLinear()
    .domain([xMin, xMax])
    .range([margin.left + padding.left, width - margin.left - padding.right]);

  const yScale = d3
    .scaleLinear()
    .domain([minRounded, maxRounded])
    .range([height - margin.bottom, margin.top]);

  const earningsDayColumnIndex = data.findIndex(
    d => d.horizon === '0d' || d.horizon === '0t'
  );

  return (
    <div className={styles.highLowChartRoot}>
      <svg
        className={styles.rootSvg}
        viewBox={`0 0 ${width} ${height}`}
        data-testid="high-low-chart"
      >
        {alt && <title>{alt}</title>}
        <g>
          {chartTicks.map(tickValue => (
            <g key={tickValue} transform={`translate(0, ${yScale(tickValue)})`}>
              <line
                className={styles.axisLine}
                x1={margin.left}
                x2={width - margin.right}
                strokeWidth={tickValue === 0 ? 2 : 1}
              />
              <text
                className={styles.axisText}
                textAnchor="end"
                x={width}
                alignmentBaseline="middle"
              >
                {formatPercentage(tickValue, {
                  suffix: '%',
                  decimals: usesSecondaryAxisStyle ? 0 : 2,
                })}
              </text>
            </g>
          ))}
        </g>

        <g transform={`translate(0, ${height})`}>
          {data.map((item, index) => (
            <text
              className={styles.axisText}
              key={`${chartId}-${index}`}
              x={xScale(index)}
              textAnchor="middle"
            >
              {item.label}
            </text>
          ))}
        </g>

        {rendersMedianConnectorOnly &&
          (() => {
            if (!data.length) {
              return null;
            }

            const y0 = yScale(0);
            const medianSegments = createLineSegmentsBetweenPoints({
              values: data.map(d => d.median),
              xForIndex: i => xScale(i),
              yForValue: v => yScale(v),
              yZero: y0,
            });

            return (
              <g key={`${chartId}-median-tracing-lines`}>
                {medianSegments.map((segment, index) => (
                  <path
                    className={clsx(styles.line, {
                      [styles.lineProfit]: segment.isAboveZero,
                    })}
                    key={`median-${index}`}
                    d={segment.path}
                    strokeDasharray="2"
                    style={{ strokeWidth: 1 }}
                  />
                ))}
              </g>
            );
          })()}

        {rendersConnectorLayer &&
          (() => {
            if (!data.length) {
              return null;
            }

            const y0 = yScale(0);
            const highSegments = createLineSegmentsBetweenPoints({
              values: data.map(d => d.high),
              xForIndex: i => xScale(i),
              yForValue: v => yScale(v),
              yZero: y0,
            });
            const lowSegments = createLineSegmentsBetweenPoints({
              values: data.map(d => d.low),
              xForIndex: i => xScale(i),
              yForValue: v => yScale(v),
              yZero: y0,
            });
            const medianSegments = createLineSegmentsBetweenPoints({
              values: data.map(d => d.median),
              xForIndex: i => xScale(i),
              yForValue: v => yScale(v),
              yZero: y0,
            });

            const createClipPath = () => {
              const points: { x: number; y: number }[] = [];

              for (let i = 0; i < data.length; i++) {
                const currentHigh = data[i].high;
                const currentX = xScale(i);
                const currentHighY = yScale(currentHigh);

                if (i === 0) {
                  points.push({ x: currentX, y: currentHighY });
                } else {
                  const prevHigh = data[i - 1].high;
                  const prevX = xScale(i - 1);
                  const prevIsHighAboveZero = prevHigh > 0;
                  const currentIsHighAboveZero = currentHigh > 0;

                  if (prevIsHighAboveZero !== currentIsHighAboveZero) {
                    const t = -prevHigh / (currentHigh - prevHigh);
                    const intersectionX = prevX + t * (currentX - prevX);
                    points.push({ x: intersectionX, y: y0 });
                  }

                  points.push({ x: currentX, y: currentHighY });
                }
              }

              for (let i = data.length - 1; i >= 0; i--) {
                const currentLow = data[i].low;
                const currentX = xScale(i);
                const currentLowY = yScale(currentLow);

                if (i === data.length - 1) {
                  points.push({ x: currentX, y: currentLowY });
                } else {
                  const nextLow = data[i + 1].low;
                  const nextX = xScale(i + 1);
                  const currentIsLowAboveZero = currentLow > 0;
                  const nextIsLowAboveZero = nextLow > 0;

                  if (currentIsLowAboveZero !== nextIsLowAboveZero) {
                    const t = -currentLow / (nextLow - currentLow);
                    const intersectionX = currentX + t * (nextX - currentX);
                    points.push({ x: intersectionX, y: y0 });
                  }

                  points.push({ x: currentX, y: currentLowY });
                }
              }

              return `M${blankSpace}${points[0].x}${blankSpace}${
                points[0].y
              }${blankSpace}${points
                .slice(1)
                .map(p => `L${blankSpace}${p.x}${blankSpace}${p.y}`)
                .join(blankSpace)}${blankSpace}Z`;
            };

            const clipPathId = `${chartId}-area-clip`;
            const clipPath = showAreaFill ? createClipPath() : '';

            return (
              <g key={`${chartId}-tracing-lines`}>
                {showAreaFill && (
                  <>
                    <defs>
                      <clipPath id={clipPathId}>
                        <path d={clipPath} />
                      </clipPath>

                      <linearGradient
                        id={`${chartId}-green-gradient`}
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                      >
                        <stop
                          offset="0%"
                          stopColor="var(--viz-bullish)"
                          stopOpacity="0.2"
                        />
                        <stop
                          offset="70%"
                          stopColor="var(--viz-bullish)"
                          stopOpacity="0.15"
                        />
                        <stop
                          offset="100%"
                          stopColor="var(--viz-bullish)"
                          stopOpacity="0.1"
                        />
                      </linearGradient>

                      <linearGradient
                        id={`${chartId}-red-gradient`}
                        x1="0%"
                        y1="100%"
                        x2="0%"
                        y2="0%"
                      >
                        <stop
                          offset="0%"
                          stopColor="var(--viz-bearish)"
                          stopOpacity="0.2"
                        />
                        <stop
                          offset="70%"
                          stopColor="var(--viz-bearish)"
                          stopOpacity="0.15"
                        />
                        <stop
                          offset="100%"
                          stopColor="var(--viz-bearish)"
                          stopOpacity="0.1"
                        />
                      </linearGradient>
                    </defs>

                    <path
                      className={clsx(styles.areaFill, {
                        [styles.areaFillProfit]: true,
                      })}
                      d={`M${blankSpace}${
                        margin.left
                      }${blankSpace}${y0}${blankSpace}L${blankSpace}${
                        width - margin.right
                      }${blankSpace}${y0}${blankSpace}L${blankSpace}${
                        width - margin.right
                      }${blankSpace}${margin.top}${blankSpace}L${blankSpace}${
                        margin.left
                      }${blankSpace}${margin.top}${blankSpace}Z`}
                      clipPath={`url(#${clipPathId})`}
                      style={{
                        fill: `url(#${chartId}-green-gradient)`,
                        fillOpacity: 1,
                      }}
                    />

                    <path
                      className={styles.areaFill}
                      d={`M${blankSpace}${
                        margin.left
                      }${blankSpace}${y0}${blankSpace}L${blankSpace}${
                        width - margin.right
                      }${blankSpace}${y0}${blankSpace}L${blankSpace}${
                        width - margin.right
                      }${blankSpace}${
                        height - margin.bottom
                      }${blankSpace}L${blankSpace}${margin.left}${blankSpace}${
                        height - margin.bottom
                      }${blankSpace}Z`}
                      clipPath={`url(#${clipPathId})`}
                      style={{
                        fill: `url(#${chartId}-red-gradient)`,
                        fillOpacity: 1,
                      }}
                    />
                  </>
                )}

                {highSegments.map((segment, index) => (
                  <path
                    className={clsx(styles.line, {
                      [styles.lineProfit]: segment.isAboveZero,
                    })}
                    key={`high-${index}`}
                    d={segment.path}
                    style={{ strokeWidth: 1 }}
                  />
                ))}

                {lowSegments.map((segment, index) => (
                  <path
                    className={clsx(styles.line, {
                      [styles.lineProfit]: segment.isAboveZero,
                    })}
                    key={`low-${index}`}
                    d={segment.path}
                    style={{ strokeWidth: 1 }}
                  />
                ))}

                {medianSegments.map((segment, index) => (
                  <path
                    className={clsx(styles.line, {
                      [styles.lineProfit]: segment.isAboveZero,
                    })}
                    key={`median-${index}`}
                    d={segment.path}
                    strokeDasharray="2"
                    style={{ strokeWidth: 1 }}
                  />
                ))}
              </g>
            );
          })()}

        {data.map((dataPoint, index) => {
          const yHigh = yScale(dataPoint.high);
          const yLow = yScale(dataPoint.low);
          const yMedian = yScale(dataPoint.median);
          const y0 = yScale(0);

          return (
            <g
              key={`${chartId}-${index}`}
              transform={`translate(${xScale(index)}, 0)`}
            >
              <HighLowBar
                yMedian={yMedian}
                yLow={yLow}
                yHigh={yHigh}
                y0={y0}
                dataPoint={dataPoint}
                showMedianLabel={variant === 'primary'}
                numberFormatter={value =>
                  formatPercentage(value, { suffix: '%' })
                }
              />
            </g>
          );
        })}

        {earningsDayColumnIndex >= 0 && (
          <line
            className={styles.axisLine}
            x1={xScale(earningsDayColumnIndex)}
            x2={xScale(earningsDayColumnIndex)}
            y1={margin.top}
            y2={height - margin.bottom}
            strokeWidth={2}
          />
        )}
      </svg>
    </div>
  );
};
